"use client"

import styled from "@emotion/styled"
import { Box, Button, IconButton, LinearProgress, Tabs } from "@mui/material"
import { GetPageQuery } from "apollo"

import { BackIcon, Container, CustomBreadcrumbs, Desktop, NewText } from "ui"
import { extractProperty, getBreadCrumbs } from "utils"
import Banner from "./Banner"
import BuildingSelection from "./BuildingSelection"
import CityProjectSelection from "./CityProjectSelection"
import DocumentDestination from "./DocumentDestination"
import DocumentDisplay from "./DocumentDisplay"
import { useFileDeliveryLogic } from "./hooks/useFileDeliveryLogic"
import SingleTab from "./SingleTab"
import {
  StyledBackStep,
  StyledBreadCrumbsWrapper,
  StyledButtonDiv,
  StyledError,
  StyledMain,
  StyledSelectedValue,
  StyledSelectedValuesContainer,
  StyledTabsContainer,
  StyledWrapper,
} from "./styles/FileDeliveryStyles"

const StyledNextButton = styled(Button)<{ isActive?: any }>`
  width: 100%;
  height: 48px;
  border-radius: 200px;
  text-decoration: none;
  font-size: 20px;
  font-weight: 600;
  border: ${(p) => (p.isActive ? "none" : "2px solid #000000")};

  background-color: ${(p) => (p.isActive ? "#2768A3" : "white")};
  color: ${(p) => (p.isActive ? "white" : "#000000")};
  :disabled {
    color: #000000;
  }
  :hover {
    text-decoration: none;
    background-color: #2768a3;
    color: white;
    border: none;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 328px;
  }
`

type Props = {
  data: GetPageQuery
  locale: string
  buildingDataResponse: GetPageQuery
  citiesDataResponse?: GetPageQuery
  projectsDataResponse?: GetPageQuery
  messages: any
}

function CustomTabPanel({
  children,
  value,
  index,
  ...other
}: {
  children?: React.ReactNode
  index: number
  value: number
}) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

const FileDelivery = ({
  data,
  locale,
  buildingDataResponse,
  citiesDataResponse,
  projectsDataResponse,
  messages,
}: Props) => {
  const { selfObject, parents } = getBreadCrumbs(data, locale)

  const heroBanner = extractProperty(data?.page, "heroBanner")?.[0]
  const categoryStepText = extractProperty(data?.page, "categoryStepText")?.[0]
  const cityProjectStepTitle = extractProperty(categoryStepText, "stepTitle")
  const cityProjectStepDescription = extractProperty(categoryStepText, "stepDescription")
  const buildingStepText = extractProperty(data?.page, "buildingStepText")?.[0]
  const buildingStepTitle = extractProperty(buildingStepText, "stepTitle")
  const buildingStepDescription = extractProperty(buildingStepText, "stepDescription")
  const destinationStepText = extractProperty(data?.page, "destinationStepText")?.[0]
  const destinationStepTitle = extractProperty(destinationStepText, "stepTitle")
  const destinationStepDescription = extractProperty(destinationStepText, "stepDescription")
  const showFileStepText = extractProperty(data?.page, "showFileStepText")?.[0]
  const showFileStepTitle = extractProperty(showFileStepText, "stepTitle")
  const fileManagementSteps = extractProperty(data?.page, "fileManagementSteps")
  const allBuildings = buildingDataResponse?.page?.children || []
  const cmsCities = citiesDataResponse?.page?.children || []
  const cmsProjects = projectsDataResponse?.page?.children || []

  const {
    value,
    selectedCity,
    selectedProject,
    cities,
    projectsForCity,
    selectedBuilding,
    filteredFiles,
    selectedDestination,
    groupedFiles,
    handleCityChange,
    handleProjectChange,
    handleBuildingChange,
    handleDestinationChange,
    handleButtonClick,
    getSelectedDisplayValues,
    isButtonActive,
    handleGoBack,
    hasError,
  } = useFileDeliveryLogic(fileManagementSteps, allBuildings, cmsCities, cmsProjects)

  const stepTitles = [
    cityProjectStepTitle,
    buildingStepTitle,
    destinationStepTitle,
    showFileStepTitle,
  ]

  const errorMessage = value === 0 ? messages.cityProjectError : messages.error

  return (
    <StyledMain>
      <Container>
        <StyledBreadCrumbsWrapper>
          <CustomBreadcrumbs current={selfObject} parents={parents} />
        </StyledBreadCrumbsWrapper>
      </Container>

      <Banner data={heroBanner} />

      <Container>
        <StyledWrapper>
          <LinearProgress variant="buffer" value={[5, 33, 65, 100][value] || 5} />

          <StyledTabsContainer>
            <Tabs
              value={value}
              sx={{
                justifyContent: "space-between",
                "& .MuiTab-root": { pointerEvents: "none" },
              }}
            >
              {stepTitles.map((_, index) => (
                <SingleTab index={index} value={value} stepTitles={stepTitles} key={index} />
              ))}
            </Tabs>

            <Desktop>
              <StyledSelectedValuesContainer>
                {Object.values(getSelectedDisplayValues()).map((displayValue, index) => (
                  <StyledSelectedValue key={index}>{displayValue}</StyledSelectedValue>
                ))}
              </StyledSelectedValuesContainer>
            </Desktop>
          </StyledTabsContainer>

          {value > 0 && (
            <StyledBackStep>
              <IconButton onClick={handleGoBack}>
                <>
                  <BackIcon />
                  <NewText component={"p"} desktopFontSize={16} color="#000000" mobileFontSize={12}>
                    {messages.back}
                  </NewText>
                </>
              </IconButton>
            </StyledBackStep>
          )}

          <CustomTabPanel value={value} index={0}>
            <CityProjectSelection
              title={cityProjectStepTitle}
              subtitle={cityProjectStepDescription}
              messages={messages}
              onCityChange={handleCityChange}
              onProjectChange={handleProjectChange}
              selectedCity={selectedCity}
              selectedProject={selectedProject}
              cities={cities}
              projects={projectsForCity}
            />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={1}>
            <BuildingSelection
              title={buildingStepTitle}
              subtitle={buildingStepDescription}
              allBuildings={allBuildings}
              selectedBuilding={selectedBuilding}
              handleBuildingChange={handleBuildingChange}
              fileManagementSteps={filteredFiles}
            />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={2}>
            <DocumentDestination
              title={destinationStepTitle}
              subtitle={destinationStepDescription}
              filteredFiles={filteredFiles}
              handleDestinationChange={handleDestinationChange}
              destionation={selectedDestination}
            />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={3}>
            <DocumentDisplay
              title={showFileStepTitle}
              filteredFiles={filteredFiles}
              groupedFiles={groupedFiles}
              messages={messages}
            />
          </CustomTabPanel>

          {value < 3 && (
            <StyledButtonDiv>
              {hasError && (
                <StyledError>
                  <NewText style={{ color: "#C92418" }} desktopFontSize={18} component={"p"}>
                    *{errorMessage}
                  </NewText>
                </StyledError>
              )}
              <StyledNextButton onClick={handleButtonClick} isActive={isButtonActive}>
                {messages.continue}
              </StyledNextButton>
            </StyledButtonDiv>
          )}
        </StyledWrapper>
      </Container>
    </StyledMain>
  )
}

export default FileDelivery
